/**
 * The panel chrome - head, body, assistant side pane - as a projection of
 * the panel store.
 *
 * A panel renders its Main/Side components as children of this frame. Its
 * store lives in the registry and outlives every mount, so a language switch
 * is one re-render rather than a rebuild, and a student who walks away and
 * comes back finds the shift they were having.
 *
 * Asynchronous because a panel's code lives in its own chunk. The student can
 * change their mind while the chunk is in flight - press Escape, walk to
 * another tool - so the effect's cleanup marks the load stale and a stale
 * chunk is dropped rather than rendered into a panel that has since closed.
 * A chunk that never arrives puts the student back on the floor.
 */

import { Component, useEffect, useMemo, useState, type ReactNode } from 'react'
import { MAINTENANCE_TOOL } from '../../fab/bays'
import { toolName } from '../../fab/layout'
import { t, tOr } from '../../i18n/i18n'
import { loadToolPanel, type BuiltPanel } from '../../tools/registry'
import type { ToolPanelHost } from '../../tools/types'
import { useLang } from '../i18n/LanguageProvider'
import { useOpenTool } from '../state/hooks'
import { panelStore } from './panelStore'

/**
 * A panel that throws while rendering takes only itself down.
 *
 * Without this a React error anywhere in a tool's panel unmounts the whole
 * tree - floor, HUD and all - and the student is left looking at a black
 * page with no way back but a reload. With it they see which panel failed
 * and can close it and walk on.
 */
class PanelBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  override componentDidCatch(error: Error) {
    console.error('Tool panel failed to render:', error)
  }

  override render() {
    if (this.state.error) {
      return <div className="cz-stats">{t('panel.Failed', { message: this.state.error.message })}</div>
    }
    return this.props.children
  }
}

export function ToolPanelFrame() {
  useLang()
  const tool = useOpenTool()
  const [built, setBuilt] = useState<BuiltPanel | null>(null)

  const host: ToolPanelHost | null = useMemo(() => {
    const services = panelStore.services
    if (!tool || !services) return null
    return { ...services, tool, close: () => panelStore.closeTool() }
  }, [tool])

  useEffect(() => {
    const services = panelStore.services
    if (!tool || !services) return
    let stale = false

    void loadToolPanel(tool.id, services, tool)
      .then((b) => {
        if (!stale) setBuilt(b)
      })
      .catch((err: unknown) => {
        console.error(`Could not open ${tool.id}`, err)
        if (!stale) panelStore.closeTool()
      })

    return () => {
      stale = true
      setBuilt(null)
    }
  }, [tool])

  const def = built
  const hasSide = def?.def.Side !== undefined

  return (
    <div className={tool ? 'panel open' : 'panel'}>
      <div className="panel-main">
        <div className="panel-head">
          {tool && (
            <>
              <span className="tag">{tool.assetTag}</span>
              <span className="title">{toolName(tool)}</span>
              <button className="btn close" onClick={() => panelStore.closeTool()}>
                {t('panel.Close')}
              </button>
            </>
          )}
        </div>
        <div className="panel-body">
          {/* The tool that is down on the floor says so here too. Its screen,
              its tower and its bay's andon all read DOWN - a technician has
              its skin off - while the panel under this line is a working
              process with a lot action at the bottom of it. Both are meant:
              the floor shows what a PM looks like, and the panel teaches the
              process the tool returns to. What was missing was anything that
              told a student the two were the same machine on the same day. */}
          {tool?.id === MAINTENANCE_TOOL && (
            <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--ink-2)', borderLeft: '3px solid #fab219', paddingLeft: 10, margin: '2px 0 14px' }}>
              {tOr(
                'tool.panel.DownForPm',
                'On the floor this tool is locked out for preventive maintenance: its skin is off and a technician is inside it, which is why its screen and the bay’s board read DOWN. What you operate here is its qualified process, the one it is released back to. A lot run from this panel is run on that process, as it will be when the tag comes off.',
              )}
            </div>
          )}
          {def && host && (
            <PanelBoundary key={tool?.id}>
              <def.def.Main store={def.store} host={host} />
            </PanelBoundary>
          )}
        </div>
      </div>
      <div className="panel-side" style={hasSide ? undefined : { display: 'none' }}>
        {def?.def.Side && host && <def.def.Side store={def.store} host={host} />}
      </div>
    </div>
  )
}
